import { useState, useEffect, useCallback, useRef, memo } from "react";
import { useNavigate } from "react-router-dom";
import { DgaButton, DgaLoading, DgaIcon } from "platformscode-new-react";
import { getSliders } from "../../services/api";
const videoSource = "assets2/videos/1.mp4";
import "./SharedStyle.scss";
import "./Slider.css";

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────
const AUTOPLAY_INTERVAL  = 10_000;
const AUTOPLAY_PAUSED    = true;   // temporary — set to false to re-enable
const LCP_CACHE_KEY      = "slider_lcp_image";
const SLIDES_CACHE_KEY   = "slider_slides_cache";

const HERO_BG_IMAGES = [
  "/assets2/imges/Hero Section.jpg",
  "/assets2/imges/hero-section-alt.jpg",
  "/assets2/imges/hero-section-1.jpg",
  "/assets2/imges/hero-section-2.jpg",
  "/assets2/imges/hero-section-3.jpg",
];

// Sanitised basenames produced by scripts/optimize-images.mjs.
// Used to build srcset strings for the <picture> element.
const HERO_WEBP_BASE = {
  "/assets2/imges/Hero Section.jpg":     "hero-section",
  "/assets2/imges/hero-section-alt.jpg": "hero-section-alt",
  "/assets2/imges/hero-section-1.jpg":   "hero-section-1",
  "/assets2/imges/hero-section-2.jpg":   "hero-section-2",
  "/assets2/imges/hero-section-3.jpg":   "hero-section-3",
};
const HERO_SRCSET_WIDTHS = [640, 1280, 1920];

const buildHeroSrcset = (imagePath, ext) => {
  const base = HERO_WEBP_BASE[imagePath];
  if (!base) return null;
  return HERO_SRCSET_WIDTHS.map(w => `/assets2/imges/${base}-${w}w.${ext} ${w}w`).join(", ");
};
const _heroBg = HERO_BG_IMAGES[0];

const HERO_TEXTS = [
  { title: "خلق رحلة روحانية خالدة",       desc: "رؤيتنا خلق رحلة روحانية خالدة تـفوق تطلعات ضيوف الرحـمن" },
  { title: "مع كل خطوة يُكتب أثر جديد",    desc: "نعمل على إثراء رحلة الحاج بتجربة متوازنة تجمع بين السهولة والروحانية" },
  { title: "نفخر بخدمة ضيوف بيت الله",     desc: "نستقبل ضيوف الرحمن بقلب مفتوح وخدمة تليق بمقامهم" },
];
const _heroText = HERO_TEXTS[Math.floor(Math.random() * HERO_TEXTS.length)];

const HERO_SECTION_SLIDE = {
  id: "hero-section-static",
  isHeroSection: true,
  image: _heroBg,
  thumbnail: _heroBg,
  whiteText: _heroBg === HERO_BG_IMAGES[0],
  title: _heroText.title,
  desc: _heroText.desc,
  logo: _heroBg === HERO_BG_IMAGES[0]
    ? "/assets2/logos/White Logo 2.svg"
    : "/assets2/logos/black Logo 2.svg",
};

// ─────────────────────────────────────────────────────────────────────────────
// LCP PRELOAD HELPER
// ─────────────────────────────────────────────────────────────────────────────
const preloadImage = (url) => {
  if (!url) return;
  if (document.querySelector(`link[rel="preload"][href="${url}"]`)) return;
  const link         = document.createElement("link");
  link.rel           = "preload";
  link.as            = "image";
  link.href          = url;
  link.fetchPriority = "high";
  document.head.prepend(link);
};

// ─────────────────────────────────────────────────────────────────────────────
// MODULE-LEVEL: runs before React mounts.
// 1. Read cache → render immediately + preload LCP image (repeat visits)
// 2. Kick off API fetch right now → overlaps with React init (first visit)
// ─────────────────────────────────────────────────────────────────────────────
let cachedSlides = null;
try {
  const raw = localStorage.getItem(SLIDES_CACHE_KEY);
  if (raw) {
    cachedSlides = JSON.parse(raw);
    if (cachedSlides?.[0]?.image) preloadImage(cachedSlides[0].image);
  }
} catch (_) { cachedSlides = null; }

// Start the network request immediately — even before the component mounts.
// On first visit this shaves ~50–150ms off the resource load delay because
// the request is in-flight while React is still initialising.
let slidersPromise = null;
try { slidersPromise = getSliders(); } catch (_) {}

// ─────────────────────────────────────────────────────────────────────────────
// DOT BUTTON — defined outside component to prevent recreation on every render
// ─────────────────────────────────────────────────────────────────────────────
const DotButton = memo(({ selected, onClick, index, total }) => (
  <button
    type="button"
    role="tab"
    className={`slider__dot${selected ? " slider__dot--selected" : ""}`}
    onClick={onClick}
    aria-label={`الشريحة ${index + 1} من ${total}`}
    aria-selected={selected}
    tabIndex={selected ? 0 : -1}
  />
));
DotButton.displayName = "DotButton";

// ─────────────────────────────────────────────────────────────────────────────
// LOADING SKELETON
// ─────────────────────────────────────────────────────────────────────────────
const SliderLoading = () => (
  <section className="slider" aria-label="جاري تحميل العرض" aria-busy="true">
    <div className="slider__viewport c-mask">
      <div className="slider__loading">
        <DgaLoading size="lg" variant="brand" />
      </div>
    </div>
  </section>
);

// ─────────────────────────────────────────────────────────────────────────────
// FALLBACK (no API data)
// ─────────────────────────────────────────────────────────────────────────────
const SliderFallback = () => (
  <section className="slider">
    <div className="slider__viewport c-mask">
      <video
        autoPlay
        muted
        loop
        playsInline
        className="slider__image"
        aria-hidden="true"
      >
        <source src={videoSource} type="video/mp4" />
      </video>
      <div className="slider__overlay">
        <div className="w-full cw-full">
          <div className="cw-full relative text-white w-full flex flex-col justify-center text-right gap-[36px] hero">
            <div className="c-ontent grid gap-[24px]">
              <h1>أمانة العاصمة المقدسة</h1>
              <p>نسعى في تقديم خدمات بلدية راقية لساكني أم القرى وقاصديها.</p>
              <DgaButton
                label="عن الأمانة"
                variant="secondary-outline"
                size="md"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
function Slider() {
  const navigate = useNavigate();
  // Seed state from module-level cache — renders instantly on repeat visits,
  // eliminating the resource load delay entirely for the LCP image.
  const [sliders,          setSliders]          = useState(cachedSlides ? [...cachedSlides, HERO_SECTION_SLIDE] : [HERO_SECTION_SLIDE]);
  const [loading,          setLoading]          = useState(false);
  const [activeIndex,      setActiveIndex]      = useState(0);
  const [readyForAdjacent, setReadyForAdjacent] = useState(false);

  const intervalRef  = useRef(null);
  const isPausedRef  = useRef(false);

  // ── Fetch ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    const controller = new AbortController();

    (async () => {
      try {
        // Re-use the module-level prefetch if it's still pending (first mount),
        // otherwise start a fresh request. Clear the reference so subsequent
        // mounts (e.g. HMR) always use a fresh fetch.
        const req = slidersPromise || getSliders(controller.signal);
        slidersPromise = null;
        const data = await req;

        const sorted = data.data
          .filter(s => s.type === "alws" || s.type === "tmp")
          .sort((a, b) => a.order - b.order);

        setSliders([HERO_SECTION_SLIDE, ...sorted]);

        // Cache for next visit's module-level preload — do NOT preload here,
        // as the API image is not visible during the LCP window (hero is active).
        if (sorted[0]?.image) {
          try {
            localStorage.setItem(LCP_CACHE_KEY, sorted[0].image);
            localStorage.setItem(SLIDES_CACHE_KEY, JSON.stringify(sorted));
          } catch (_) {}
        }
      } catch (err) {
        if (err.name !== "AbortError") console.error("Slider fetch error:", err);
      } finally {
        setLoading(false);
      }
    })();

    return () => controller.abort();
  }, []);

  // ── Defer adjacent slide image loading ────────────────────────────────────
  // Prevents API slide images (opacity:0, off-screen) from downloading during
  // the LCP window, which would update LCP to the API response time (~2.7s).
  useEffect(() => {
    const t = setTimeout(() => setReadyForAdjacent(true), 3500);
    return () => clearTimeout(t);
  }, []);

  // ── Autoplay ───────────────────────────────────────────────────────────────
  const stopAutoplay = useCallback(() => {
    clearInterval(intervalRef.current);
  }, []);

  const startAutoplay = useCallback(() => {
    stopAutoplay();
    if (isPausedRef.current || AUTOPLAY_PAUSED) return;
    intervalRef.current = setInterval(() => {
      setActiveIndex(prev => (prev + 1) % sliders.length);
    }, AUTOPLAY_INTERVAL);
  }, [sliders.length, stopAutoplay]);

  useEffect(() => {
    if (sliders.length > 1) startAutoplay();
    return stopAutoplay;
  }, [sliders.length, startAutoplay, stopAutoplay]);

  // ── Navigation ─────────────────────────────────────────────────────────────
  const goToSlide = useCallback((index) => {
    setActiveIndex(index);
    startAutoplay(); // restart timer on manual navigation
  }, [startAutoplay]);

  // ── Pause on hover / focus-within ──────────────────────────────────────────
  const handleMouseEnter  = useCallback(() => { isPausedRef.current = true;  stopAutoplay(); },  [stopAutoplay]);
  const handleMouseLeave  = useCallback(() => { isPausedRef.current = false; startAutoplay(); }, [startAutoplay]);
  const handleFocusIn     = useCallback(() => { isPausedRef.current = true;  stopAutoplay(); },  [stopAutoplay]);
  const handleFocusOut    = useCallback(() => { isPausedRef.current = false; startAutoplay(); }, [startAutoplay]);

  // ── Keyboard: arrow keys navigate between slides ───────────────────────────
  const handleKeyDown = useCallback((e) => {
    if (e.key === "ArrowLeft")  goToSlide((activeIndex + 1) % sliders.length);
    if (e.key === "ArrowRight") goToSlide((activeIndex - 1 + sliders.length) % sliders.length);
  }, [activeIndex, sliders.length, goToSlide]);

  // ── Determine whether a slide's image should be in the DOM ───────────────
  // Only active ±1 slides (with wrap-around) keep their <img>.
  // Adjacent images are deferred until after the LCP window to prevent API
  // slide images from becoming LCP candidates before the hero paints.
  const shouldRenderImage = useCallback((index) => {
    const len  = sliders.length;
    const dist = Math.min(Math.abs(index - activeIndex), len - Math.abs(index - activeIndex));
    return readyForAdjacent ? dist <= 1 : dist === 0;
  }, [activeIndex, sliders.length, readyForAdjacent]);

  // ── Slide link handler ─────────────────────────────────────────────────────
  const handleSliderClick = useCallback((slider) => {
    if (slider.sli_is_deti === "yes") {
      navigate(`/ad-details/${slider.id}`);
    } else if (slider.link?.trim()) {
      if (slider.link.startsWith("http")) {
        window.open(slider.link, "_blank", "noopener,noreferrer");
      } else {
        navigate(slider.link);
      }
    }
  }, [navigate]);

  // ── Render states ──────────────────────────────────────────────────────────
  if (loading)         return <SliderLoading />;
  if (!sliders.length) return <SliderFallback />;

  return (
    <section
      className="slider"
      aria-label="عرض الشرائح"
      aria-roledescription="carousel"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocusIn}
      onBlur={handleFocusOut}
      onKeyDown={handleKeyDown}
    >
      {/* Visually hidden live region — announces active slide to screen readers */}
      <div className="slider__live" aria-live="polite" aria-atomic="true">
        {`الشريحة ${activeIndex + 1} من ${sliders.length}`}
      </div>

      <div className="slider__viewport c-mask">
        <div className="slider__track">
          {sliders.map((slider, index) => (
            <div
              key={slider.id}
              className={`slider__slide${index === activeIndex ? " slider__slide--active" : ""}`}
              role="group"
              aria-roledescription="slide"
              aria-label={slider.title || `الشريحة ${index + 1}`}
              aria-hidden={index !== activeIndex || undefined}
              inert={index !== activeIndex}
            >
              {/* Slide image — only rendered for active ±1 to avoid downloading off-screen images */}
              {shouldRenderImage(index) ? (
                <picture>
                  {/* Hero section: serve locally-optimised AVIF/WebP srcset */}
                  {slider.isHeroSection && (() => {
                    const avifSrcset = buildHeroSrcset(slider.image, "avif");
                    const webpSrcset = buildHeroSrcset(slider.image, "webp");
                    return (
                      <>
                        {avifSrcset && <source type="image/avif" srcSet={avifSrcset} sizes="100vw" />}
                        {webpSrcset && <source type="image/webp" srcSet={webpSrcset} sizes="100vw" />}
                      </>
                    );
                  })()}
                  {/* API slides: mobile thumbnail fallback */}
                  {!slider.isHeroSection && slider.thumbnail && (
                    <source media="(max-width: 768px)" srcSet={slider.thumbnail} />
                  )}
                  <img
                    src={slider.image}
                    alt={slider.title || ""}
                    className="slider__image"
                    loading={index === 0 ? "eager" : "lazy"}
                    fetchPriority={index === 0 ? "high" : "auto"}
                    decoding={index === 0 ? "sync" : "async"}
                    width="1920"
                    height="1080"
                  />
                </picture>
              ) : (
                <div className="slider__image" aria-hidden="true" />
              )}

              {/* Slide content */}
              {slider.isHeroSection ? (
                <div className="slider__overlay bg-header">

                  <div className="slider__text-panel slider__text-panel--hero">
                       <img
                    src={slider.logo}
                    alt=""
                    className="slider__hero-logo"
                    aria-hidden="true"
                    width="117"
                    height="76"
                  />
                    <div className={`slider__inner-content hero text-right${slider.whiteText ? " text-white" : ""}`}>
                      <h1 className="md:max-w-[700px] !leading-relaxed slider__hero-title">{slider.title}</h1>
                      {slider.desc && (
                        <p className="md:max-w-[600px] !leading-relaxed">{slider.desc}</p>
                      )}
                    </div>
                  </div>
                </div>
              ) : slider.isCustom ? (
                <div className="slider__overlay">
                  <div className="c-ontent pb-[40px] w-full flex flex-col gap-[12px] md:flex-row md:justify-between md:items-start slider-container">
                    <div className="text-right text-white flex flex-col gap-[12px]">
                      <h1 className="md:max-w-[600px] !leading-relaxed">{slider.title}</h1>
                      {slider.desc && <p className="md:max-w-[600px] !leading-relaxed">{slider.desc}</p>}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="slider__overlay bg-header">
                  <div className="slider__text-panel">
                    <div className="slider__inner-content hero text-white text-right">
                      <h1 className="md:max-w-[700px] !leading-relaxed">{slider.title}</h1>
                      {slider.desc && (
                        <p className="md:max-w-[600px] !leading-relaxed">{slider.desc}</p>
                      )}
                      {(slider.hasLink || slider.sli_is_deti === "yes") && (
                        <button
                          type="button"
                          className="btn-secondary-solid"
                          onClick={() => handleSliderClick(slider)}
                          aria-label={`المزيد عن ${slider.title}`}
                        >
                          المزيد
                          {slider.sli_url_is_extarnal === "yes" && (
                            <DgaIcon
                              icon="link-square-02"
                              size={16}
                              type="standard"
                              variant="stroke"
                              aria-hidden="true"
                            />
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Dots */}
        {sliders.length > 1 && (
          <div className="slider__dots" role="tablist" aria-label="التنقل بين الشرائح">
            {sliders.map((_, index) => (
              <DotButton
                key={index}
                index={index}
                total={sliders.length}
                selected={index === activeIndex}
                onClick={() => goToSlide(index)}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export default Slider;
